import { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Context } from '../../context/context';
import DetailsDialog from './DetailsDialog';
import DateFormat from '../../utils/DateFormat';
import api from '../../api'
import { updateUserWorkDays } from '../../context/actions';
import {
    Views,
} from 'react-big-calendar'
import TimeKeepingCalendar from './TimeKeeperCalendar/TimeKeepingCalendar';
import CalendarColors from './TimeKeeperCalendar/CalendarColors';
import { useTranslation } from 'react-i18next';
import TimeCalculator from '../../utils/TimeCalculator';

const findPrevCurrentNext = (day, workDays) => {
    let date = DateFormat.formatISOStringWithTimeZone(day).split('T')[0];

    let current = workDays.find(wd => {
        let d = DateFormat.formatISOStringWithTimeZone(wd.date).split('T')[0];

        if (d === date) {
            return true;
        };
        return false;
    });

    let yesterday = new Date(day);
    yesterday.setDate(yesterday.getDate() + 1);
    yesterday = DateFormat.formatISOStringWithTimeZone(yesterday).split('T')[0];

    let tomorrow = new Date(day);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow = DateFormat.formatISOStringWithTimeZone(tomorrow).split('T')[0];

    let prev = workDays.find(wd => {
        let d = DateFormat.formatISOStringWithTimeZone(wd.date).split('T')[0];

        if (d === yesterday) {
            return true;
        };
        return false;
    });

    let next = workDays.find(wd => {
        let d = DateFormat.formatISOStringWithTimeZone(wd.date).split('T')[0];

        if (d === tomorrow) {
            return true;
        };
        return false;
    });

    return { prev, current, next };
};

function TimeKeepingPage() {
    const { t } = useTranslation()
    const [{ user }, dispatch] = useContext(Context);
    const [entries, setEntries] = useState([]);
    const [workDays, setWorkDays] = useState(user?.workDays);

    const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);
    const [location, setLocation] = useState(null);

    const [selectedWorkDay, setSelectedWorkDay] = useState(null);
    const [lastEnd, setLastEnd] = useState(null);
    const [nextStart, setNextStart] = useState(null);
    const [defaultDate, setDefaultDate] = useState(new Date());
    const [currentFlexTimeAccount, setCurrentFlexTimeAccount] = useState(null);

    const { id } = useParams();

    const mapWorkDay = (workDay) => workDay.workEntries.map((we, index) => {
        return {
            start: we.start,
            end: we.end,
            id: workDay.id,
            type: 'WORK_ENTRY',
            submitted: workDay.submitted,
            isLast: index === workDay.workEntries.length - 1,
        };
    });

    useEffect(() => {
        let e = user.workDays.map(wd => mapWorkDay(wd));
        e = e.flat()

        if(user.contractInfo) {
            let dayHeaders = user.workDays.filter(wd => wd.date.getTime() >= user.startOfFlexTimeAccount.getTime()).map(wd => {
                let actual  = TimeCalculator.computeWorkDurations(wd.workEntries).sumWorkDurations + wd.compensation
                return {
                    start: wd.date,
                    end : wd.date,
                    contractWorkLoad : wd.contractWorkLoad,
                    actual : actual,
                    difference : actual - wd.contractWorkLoad,
                    allDay : true,
                    type : "DAY_SUMMARY"
                };
            });

            e.push(...dayHeaders);
        }
        
        setEntries(e);

        setWorkDays(user.workDays.map(wd => {
            return {
                ...wd,
                type: "WORKDAY",
            };
        }));

        setLocation(user.location);
        if (user.id !== '') {
            if (user.contractInfo) {
                api.readCurrentFlexTimeAccount(user.id).then(res => {
                    setCurrentFlexTimeAccount(res)
                });
            };
        };
    }, [user]);

    useEffect(() => {
        if (id) {
            let wd = workDays.find(wd => wd.id === id);

            if (wd) {
                let { prev, current, next } = findPrevCurrentNext(wd.date, workDays);
                setLastEnd(prev ? prev.end : null);
                setNextStart(next ? next.start : null);
                setSelectedWorkDay(current);
                setIsDetailsDialogOpen(true);
                setDefaultDate(wd.date);
            };
        };
    }, [id]);

    const onSelectEvent = ({ id }) => {
        let workDay = user.workDays.find(wd => wd.id === id);
        setSelectedWorkDay(workDay);
        setIsDetailsDialogOpen(true);
    };

    const onSelectSlot = (slotInfo) => {
        let { prev, current, next } = findPrevCurrentNext(slotInfo.start, workDays);

        setLastEnd(prev ? prev.end : null);
        setNextStart(next ? next.start : null);

        if (!current) {
            api.createWorkDay(user.id, {
                date: slotInfo.start,
                workEntries: [],
            }).then(res => {
                api.readUser(user.id).then(res => {
                    let { prev, current, next } = findPrevCurrentNext(slotInfo.start, res.workDays);
                    setLastEnd(prev ? prev.end : null);
                    if (slotInfo.end - slotInfo.start > 1000 * 60 * 30) {
                        setSelectedWorkDay({
                            ...current,
                            start: slotInfo.start,
                            end: slotInfo.end,
                        });
                    } else {
                        setSelectedWorkDay(current);
                    }
                    dispatch(updateUserWorkDays(res.workDays));
                });
            });
        } else if (!current.start && !current.submitted) {
            if (slotInfo.end - slotInfo.start > 1000 * 60 * 30) {
                setSelectedWorkDay({
                    ...current,
                    start: slotInfo.start,
                    end: slotInfo.end,
                });
            } else {
                setSelectedWorkDay(current);
            }

        } else {
            setSelectedWorkDay(current);
        };

        setIsDetailsDialogOpen(true);
    };

    const handleOnCloseDetailsDialog = () => {
        setIsDetailsDialogOpen(false);
        setSelectedWorkDay(null);
    };

    const handleUpdateWorkDay = (wd) => {
        api.updateWorkDay(user.id, wd.id, wd).then(res => {
            api.readUser(user.id).then(res => {
                dispatch(updateUserWorkDays(res.workDays));
            });
        });

        handleOnCloseDetailsDialog();
    };

    return (
        <>
            <TimeKeepingCalendar
                workDays={workDays}
                workEntries={entries}
                date={defaultDate}
                onSelectEvent={onSelectEvent}
                onNavigate={(newDate) => setDefaultDate(newDate)}
                onSelectSlot={onSelectSlot}
                location={location}
                contractInfo={user?.contractInfo}
                currentFlexTimeAccount={currentFlexTimeAccount}
                startOfFlexTimeAccount={user.startOfFlexTimeAccount}
                legend={[
                    { label: t('feiertag', { ns: 'timeKeepingPage', defaultValue: 'Feiertag' }), color: CalendarColors.get('holiday') },
                    { label: t('brückentag', { ns: 'timeKeepingPage', defaultValue: 'Brückentag' }), color: CalendarColors.get('closingDay') },
                    { label: t('heute', { ns: 'timeKeepingPage', defaultValue: 'Heute' }), color: CalendarColors.get('today') },
                    { label: user?.contractInfo ? t('arbeitsfrei', { ns: 'timeKeepingPage', defaultValue: 'Arbeitsfreier Tag' }) : t('wochenende', { ns: 'timeKeepingPage', defaultValue: 'Wochenende' }), color: CalendarColors.get('offWork') },
                    { label: t('pause', { ns: 'timeKeepingPage', defaultValue: 'Pause' }), color: CalendarColors.get('workDay') },
                    { label: t('arbeit', { ns: 'timeKeepingPage', defaultValue: 'Arbeit' }), color: CalendarColors.get('workEntry') },
                ]}
                view={Views.WEEK}
                views={[Views.WEEK]}
            />
            {selectedWorkDay &&
                <DetailsDialog
                    open={isDetailsDialogOpen}
                    onClose={handleOnCloseDetailsDialog}
                    workDay={selectedWorkDay}
                    onSave={handleUpdateWorkDay}
                    lastDayEnd={lastEnd}
                    nextDayStart={nextStart}
                    isFlexTime={user.hasFlexTimeAccount}
                    contractInfo={user.contractInfo}
                />
            }
        </>
    );
};

export default TimeKeepingPage
